import { useNotification } from 'lib/context/notification.context'
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { createZipReportJob, getZipReportJobStatus, ZipReportJobStatus } from 'services/common/reports/getProcessReport/get-process-reports-service'
import styled from 'styled-components'

const JOB_KEY = 'activeZipReportJobId'
const DOWNLOADED_KEY = 'lastDownloadedZipReportJobId'

const Panel = styled.aside<{ $expanded: boolean }>`
  position: fixed;
  z-index: 1300;
  right: 20px;
  bottom: 20px;
  width: min(410px, calc(100vw - 40px));
  height: ${({ $expanded }) => $expanded ? 'min(720px, calc(100vh - 40px))' : 'auto'};
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 18px;
  background: #fff;
  border: 1px solid #d9d9e3;
  border-radius: 12px;
  box-shadow: 0 12px 35px rgba(22, 50, 71, 0.2);
  color: #163247;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  button {
    padding: 0;
    border: 0;
    background: none;
    color: #2d749c;
    font: inherit;
    cursor: pointer;
    white-space: nowrap;
  }
`

const Progress = styled.div<{ $percent: number }>`
  height: 8px;
  margin-top: 12px;
  border-radius: 8px;
  background: linear-gradient(to right, #c6016b ${({ $percent }) => `${$percent}%`}, #ececf3 0);
`

const ResultBar = styled.div`
  display: flex;
  flex-shrink: 0;
  height: 10px;
  margin-top: 12px;
  overflow: hidden;
  border-radius: 8px;
  background: #ececf3;

  span { height: 100%; }
`

const ResultLegend = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-top: 8px;
  font-size: 12px;

  span { display: inline-flex; align-items: center; gap: 6px; }
  i { width: 10px; height: 10px; border-radius: 50%; }
`

const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 12px;
  margin-top: 12px;

  button, a {
    padding: 8px 12px;
    border: 1px solid #a9bed0;
    border-radius: 6px;
    background: #fff;
    color: #2d749c;
    font: inherit;
    cursor: pointer;
    text-decoration: none;
  }
`

const Results = styled.div<{ $expanded: boolean }>`
  margin-top: 12px;
  max-height: ${({ $expanded }) => $expanded ? 'none' : '240px'};
  min-height: 0;
  flex: ${({ $expanded }) => $expanded ? '1 1 auto' : '0 1 auto'};
  overflow-y: auto;

  ul { margin: 8px 0 0; padding-left: 20px; }
  li { margin-bottom: 8px; }
  small { display: block; }
`

type ZipReportContextValue = {
  job: ZipReportJobStatus | null
  starting: boolean
  start: (processIds: number[]) => Promise<void>
  dismiss: () => void
}

const ZipReportContext = createContext<ZipReportContextValue | null>(null)

const startDownload = (url: string) => {
  const link = document.createElement('a')
  link.href = url
  link.download = ''
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export const ZipReportJobProvider = ({ children }: { children: ReactNode }) => {
  const { isAuth, role } = useSelector((state: any) => state.auth)
  const { getError, getSuccess, getWarning } = useNotification()
  const [job, setJob] = useState<ZipReportJobStatus | null>(null)
  const [starting, setStarting] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const notifiedJobId = useRef<number | null>(null)
  const canTrack = isAuth && ['special_admin', 'super_admin', 'platform_admin', 'super_platform_admin'].includes(role)

  const update = useCallback((current: ZipReportJobStatus) => {
    setJob(current)
    if (current.status === 'completed' && current.file_link && notifiedJobId.current !== current.id) {
      notifiedJobId.current = current.id
      if (localStorage.getItem(DOWNLOADED_KEY) !== String(current.id)) {
        startDownload(current.file_link)
        localStorage.setItem(DOWNLOADED_KEY, String(current.id))
      }
      getSuccess('ZIP listo. Si la descarga no comienza, pulsa «Descargar ZIP».')
      if (current.failed_reports > 0) getWarning(`El ZIP contiene ${current.failed_reports} informes con errores.`)
    }
    if (current.status === 'failed' && notifiedJobId.current !== current.id) {
      notifiedJobId.current = current.id
      getError(current.error_message || 'No se pudo generar el ZIP de reportes.')
    }
  }, [getError, getSuccess, getWarning])

  const refresh = useCallback(async (jobId: number) => {
    try {
      const response = await getZipReportJobStatus(jobId)
      if (localStorage.getItem(JOB_KEY) !== String(jobId)) return
      const current: ZipReportJobStatus = response?.data || response
      if (!current?.id) throw new Error('Estado del ZIP no disponible.')
      update(current)
    } catch (error: any) {
      if (error?.response?.status === 403 || error?.response?.status === 404) {
        localStorage.removeItem(JOB_KEY)
        setJob(null)
      }
      console.error('Error consultando el ZIP', error)
    }
  }, [update])

  const download = async (jobId: number) => {
    try {
      const response = await getZipReportJobStatus(jobId)
      const current: ZipReportJobStatus = response?.data || response
      if (current.status !== 'completed' || !current.file_link) {
        throw new Error('El ZIP ya no está disponible.')
      }
      startDownload(current.file_link)
    } catch {
      getError('No se pudo obtener una descarga vigente del ZIP.')
    }
  }

  useEffect(() => {
    if (!canTrack) return
    const stored = Number(localStorage.getItem(JOB_KEY))
    if (Number.isInteger(stored) && stored > 0) refresh(stored)
  }, [canTrack, refresh])

  useEffect(() => {
    if (!canTrack || !job || !['pending', 'processing'].includes(job.status)) return
    const timer = window.setInterval(() => refresh(job.id), 5000)
    return () => window.clearInterval(timer)
  }, [canTrack, job, refresh])

  const start = async (processIds: number[]) => {
    if (starting || (job && ['pending', 'processing'].includes(job.status))) {
      getWarning('Ya se está generando un ZIP. Consulta el progreso en el panel superior.')
      return
    }
    setStarting(true)
    setExpanded(false)
    try {
      const response = await createZipReportJob(processIds)
      const current: ZipReportJobStatus = response?.data || response
      if (!current?.id) throw new Error('No se pudo iniciar la generación del ZIP.')
      localStorage.setItem(JOB_KEY, String(current.id))
      notifiedJobId.current = null
      update(current)
    } catch (error) {
      console.error('Error iniciando el ZIP', error)
      getError('No se pudo iniciar la generación del ZIP de reportes.')
    } finally {
      setStarting(false)
    }
  }

  const dismiss = () => {
    if (job && ['pending', 'processing'].includes(job.status)) return
    localStorage.removeItem(JOB_KEY)
    setJob(null)
    setExpanded(false)
  }

  const running = job && ['pending', 'processing'].includes(job.status)
  const processed = (job?.completed_reports || 0) + (job?.failed_reports || 0)
  const processCount = job?.process_ids?.length || 0
  const results = job?.process_results || []
  const correctProcesses = results.filter((result) => result.total_reports > 0 && result.failed_reports === 0).length
  const affectedProcesses = results.length - correctProcesses

  return (
    <ZipReportContext.Provider value={{ job, starting, start, dismiss }}>
      {children}
      {canTrack && job && (
        <Panel role="status" aria-live="polite" $expanded={expanded && !running && results.length > 0}>
          <PanelHeader>
            <strong>ZIP de reportes por usuario</strong>
            {!running && results.length > 0 && <button type="button" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-controls="zip-report-results">
              {expanded ? 'Reducir' : 'Ampliar resultados'}
            </button>}
          </PanelHeader>
          <div>{job.status === 'pending'
            ? `Analizando ${processCount} procesos seleccionados...`
            : running
            ? `Procesados ${processed} de ${job.total_reports} informes en ${processCount} procesos`
            : job.status === 'completed'
            ? results.length > 0
              ? `ZIP listo. ${results.length} ${results.length === 1 ? 'proceso' : 'procesos'} en total: ${correctProcesses} ${correctProcesses === 1 ? 'correcto' : 'correctos'} y ${affectedProcesses} con incidencias.`
              : `ZIP listo: ${job.completed_reports} informes generados en ${processCount} procesos seleccionados`
            : `No se pudo generar el ZIP de ${processCount} procesos`}</div>
          {running && <div>Puedes seguir trabajando. Mantén abierta esta pestaña para la descarga automática.</div>}
          {job.progress_message && <div>{job.progress_message}</div>}
          {!running && results.length > 0 ? <>
            <ResultBar role="img" aria-label={`${correctProcesses} procesos correctos y ${affectedProcesses} con incidencias, de ${results.length} en total`}>
              <span style={{ width: `${(correctProcesses / results.length) * 100}%`, backgroundColor: '#16803d' }} />
              <span style={{ width: `${(affectedProcesses / results.length) * 100}%`, backgroundColor: '#c93636' }} />
            </ResultBar>
            <ResultLegend aria-hidden="true">
              <span><i style={{ backgroundColor: '#16803d' }} />Correctos ({correctProcesses})</span>
              <span><i style={{ backgroundColor: '#c93636' }} />Con incidencias ({affectedProcesses})</span>
            </ResultLegend>
          </> : <Progress $percent={job.progress_percent || 0} role="progressbar" aria-valuenow={job.progress_percent || 0} aria-valuemin={0} aria-valuemax={100} />}
          {!running && results.length > 0 && <Results id="zip-report-results" $expanded={expanded}>
            <strong>Resultado por proceso</strong>
            <ul>{results.map((result) => <li key={result.process_id}>
              <strong>{result.process_name}</strong>: {result.total_reports === 0
                ? 'Sin informes'
                : result.failed_reports === 0
                ? `Correcto (${result.completed_reports} informes)`
                : result.completed_reports > 0
                ? `Parcial (${result.completed_reports} correctos, ${result.failed_reports} fallidos)`
                : `Incorrecto (${result.failed_reports} fallidos)`}
              {result.reason && <small>{result.reason}</small>}
            </li>)}</ul>
          </Results>}
          {!running && <Actions>
            {job.status === 'completed' && job.file_link && <button type="button" onClick={() => download(job.id)}>Descargar ZIP</button>}
            <button type="button" onClick={dismiss}>Cerrar</button>
          </Actions>}
        </Panel>
      )}
    </ZipReportContext.Provider>
  )
}

export const useZipReportJob = () => {
  const context = useContext(ZipReportContext)
  if (!context) throw new Error('No existe el contexto del ZIP')
  return context
}
